import axios, { AxiosError } from "axios";
import { useMutation, useQueryClient } from "react-query";
import { STAC_URL, API_VERSION, INMA_URL } from "utils/constants";
import { QUERY_KEYS } from "utils/requests";
import { PanelMode, useDeleteOptions } from "../types";
import { IStacCollection } from "../../../types/stac";
import { useGlobalToaster } from "components/controls/toastNotification";
import { useState } from "react";
import { checkOperationStatus } from "../common/hooks";
import DeletePanel from "./Collection.DeletePanel";
import { useNavigateWithParams } from "utils/components/LinkWithParams";

const INMA_STATUS = {
  PENDING: "Pending",
  FAILED: "Failed",
  FINISHED: "Finished",
  RUNNING: "Running",
  CANCELED: "Canceled",
  SUCCEEDED: "Succeeded",
};

export const useUpsertCollection = (mode?: PanelMode) => {
  const queryClient = useQueryClient();
  const { addToast, removeToast } = useGlobalToaster();

  return useMutation(
    async (collection: IStacCollection) => {
      if (mode === "create") {
        return await createCollection(collection);
      } else {
        return await updateCollection(collection);
      }
    },
    {
      onSuccess: async (data: string | IStacCollection) => {
        if (mode === "create") {
          // Handle async create operation (returns operation location)
          const operationLocation = data as string;
          const message = "Collection creation in progress...";
          addToast(
            message,
            "Pending",
            undefined,
            "info",
            undefined,
            operationLocation
          );

          const interval = setInterval(async () => {
            try {
              const statusData = await checkOperationStatus(operationLocation);
              if (
                statusData.status === INMA_STATUS.PENDING ||
                statusData.status === INMA_STATUS.RUNNING
              ) {
                return;
              }
              if (
                statusData.status === INMA_STATUS.FINISHED ||
                statusData.status === INMA_STATUS.SUCCEEDED
              ) {
                clearInterval(interval);
                removeToast(operationLocation);
                const result = `Collection ${statusData.collectionId} creation successful`;
                addToast(result, "Collection created", statusData.collectionId);
                queryClient.invalidateQueries(QUERY_KEYS.collections);
              } else if (
                statusData.status === INMA_STATUS.FAILED ||
                statusData.status === INMA_STATUS.CANCELED
              ) {
                clearInterval(interval);
                removeToast(operationLocation);
                const errorMessage = statusData.statusHistory?.find(
                  (item: any) => item.errorMessage
                )?.errorMessage;
                addToast(
                  "Failed to create collection",
                  "Failed",
                  undefined,
                  "error",
                  errorMessage || ""
                );
              }
            } catch (error) {
              clearInterval(interval);
              removeToast(operationLocation);
              addToast(
                "Failed to create collection",
                "Failed",
                undefined,
                "error",
                error as string
              );
            }
          }, 2000);
        } else {
          // Handle sync update operation (returns collection directly)
          const collection = data as IStacCollection;
          addToast(
            `Collection ${collection.id} updated successfully`,
            "Collection updated",
            collection.id
          );
          queryClient.invalidateQueries(QUERY_KEYS.collections);
        }
      },
      onError: (error: AxiosError) => {
        addToast(
          `Failed to ${mode === "create" ? "create" : "update"} collection`,
          "Failed",
          undefined,
          "error",
          error.message + " " + JSON.stringify(error?.response?.data)
        );
      },
    }
  );
};

const getOperationLocation = (resp: import("axios").AxiosResponse): string => {
  const location = resp.headers["location"] || resp.headers["operation-location"];
  if (location) return location;
  // Fallback: construct URL from the operation ID in the response body
  const operationId = resp.data?.id || resp.data?.operationId;
  if (operationId) {
    return `${INMA_URL}/operations/${operationId}?api-version=${API_VERSION}`;
  }
  throw new Error("Could not determine operation location from response");
};

const createCollection = async (collection: IStacCollection): Promise<string> => {
  const resp = await axios.post(`${STAC_URL}/collections`, collection, {
    params: { "api-version": API_VERSION },
  });
  if (resp.status === 202) {
    return getOperationLocation(resp);
  } else {
    throw new Error("Failed to create collection");
  }
};

const updateCollection = async (
  collection: IStacCollection
): Promise<IStacCollection> => {
  const resp = await axios.put(
    `${STAC_URL}/collections/${collection.id}`,
    collection,
    { params: { "api-version": API_VERSION } }
  );
  if (resp.status === 200) {
    return resp.data;
  } else {
    throw new Error("Failed to update collection");
  }
};

/**
useDeleteCollection # noqa: E501

Hook to handle the deletion of a STAC collection with polling for operation status.

:param collection: The STAC collection to be deleted.
:type collection: IStacCollection
:param options: Options for deletion, including navigation behavior after deletion.
:type options: useDeleteOptions
:return: An object containing mutation, dialog visibility state, and delete dialog component.
:rtype: { mutation: object, showDeleteDialog: boolean, setShowDeleteDialog: function, deleteDialog: JSX.Element }
:raises AxiosError: If the deletion request fails.

References:
- `useMutation` from `react-query` for handling asynchronous operations.
- `DeletePanel` component for rendering the confirmation dialog.
*/
export const useDeleteCollection = (
  collection: IStacCollection,
  options: useDeleteOptions = {}
) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigateWithParams();
  const fn = deleteCollectionWithPolling;
  const { addToast, removeToast } = useGlobalToaster();

  const mutation = useMutation(fn, {
    onSuccess: async (data: string) => {
      addToast(
        "Collection deletion in progress...",
        "Pending",
        undefined,
        "info",
        undefined,
        data
      );
      const interval = setInterval(async () => {
        try {
          const statusData = await checkOperationStatus(data);
          if (
            statusData.status === INMA_STATUS.PENDING ||
            statusData.status === INMA_STATUS.RUNNING
          ) {
            return;
          }
          if (
            statusData.status === INMA_STATUS.FINISHED ||
            statusData.status === INMA_STATUS.SUCCEEDED
          ) {
            clearInterval(interval);
            removeToast(data);
            if (options.navigateTo) {
              navigate(options.navigateTo);
            }
            addToast(
              "Collection " + collection.id + " deleted successfully",
              "Deleted"
            );
            queryClient.invalidateQueries(QUERY_KEYS.collections);
          } else if (
            statusData.status === INMA_STATUS.FAILED ||
            statusData.status === INMA_STATUS.CANCELED
          ) {
            clearInterval(interval);
            removeToast(data);
            // Check if errorMessage exists
            const errorMessage = statusData.statusHistory.find(
              (item: any) => item.errorMessage
            )?.errorMessage;
            addToast(
              "Failed to delete collection",
              "Failed",
              undefined,
              "error",
              errorMessage || ""
            );
          }
        } catch (error) {
          clearInterval(interval);
          removeToast(data);
          addToast(
            "Failed to delete collection",
            "Failed",
            undefined,
            "error",
            error as string
          );
        }
      }, 2000);
    },
    onError: (error: AxiosError) => {
      if (error.response?.status === 409) {
        const counts = (error.response?.data as any)?.error
          ?.activeOperationCounts as Record<string, number> | undefined;
        const subtitle =
          counts && Object.keys(counts).length > 0
            ? Object.entries(counts)
                .map(([status, n]) => `${n} ${status}`)
                .join(", ")
            : "This collection has in-progress operations. Go to the Operations tab to cancel them first.";
        addToast(
          "Cannot delete collection",
          "Active operations in progress",
          collection.id,
          "error",
          subtitle,
          undefined,
          "Go to Operations",
          `/collections/${collection.id}#operations`
        );
      } else {
        addToast(
          "Failed to delete collection",
          "Failed",
          undefined,
          "error",
          error.message + " " + JSON.stringify(error?.response?.data)
        );
      }
    },
  });

  const handleConfirmDelete = () => {
    setShowDeleteDialog(false);
    mutation.mutate(collection.id);
  };

  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
  };

  const deleteDialog = (
    <DeletePanel
      collection={collection}
      onDismiss={handleCancelDelete}
      onConfirm={handleConfirmDelete}
    />
  );

  return {
    mutation,
    showDeleteDialog,
    setShowDeleteDialog,
    deleteDialog,
  };
};

const deleteCollectionWithPolling = async (collectionId: string) => {
  const resp = await axios.delete(`${STAC_URL}/collections/${collectionId}`, {
    params: { "api-version": API_VERSION },
  });
  if (resp.status === 202) {
    return getOperationLocation(resp);
  } else {
    throw new Error("Failed to delete collection");
  }
};
